import React, { useState, memo } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner/spinner';

import type { ImagePickerComponentProps } from '../pageCoverPictureTypes';

import { AddHeaderImageButton } from './AddHeaderImageButton';
import { ImageRepositioner } from './ImageRepositioner';
import { HoverOverlayAndDropdownItems } from './HoverOverlayAndDropdownItems';
import { AIUnsplashHeaderToolbar } from './AIUnsplashHeaderToolbar';

type UploadingPictureWrapperProps = {
	hasCoverPicture: boolean;
	dynamicCoverImageHeight?: number;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UploadingPictureWrapper = styled.div<UploadingPictureWrapperProps>({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => `${props.dynamicCoverImageHeight}px`,
	position: 'relative',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	background: token('color.background.accent.gray.subtlest'),
	borderRadius: '3px',
});

export const ImagePicker = memo(
	({
		hasCoverPicture,
		isTitleHovered,
		openMediaBrowser,
		onRemoveImage,
		userOffline,
		setIsUploadingPicture,
		isUploadingPicture,
		handleUnsplashUpload,
		setIsModalOpen,
		isModalOpen,
		initialPicturePosition,
		onSaveReposition,
		shouldShowReposition,
		setShouldShowReposition,
		coverPictureState,
		floatingToolbarButtonStyle,
		forceUpdate,
		contentId,
		dynamicCoverImageHeight,
		onImageWidthChange,
		spaceKey,
		onRegenerate,
		isRegeneratingImage,
		onAcceptAIUnsplashImage,
		shouldShowAIUnsplashHeaderToolbar,
		isFixedWidthImageOption,
	}: ImagePickerComponentProps) => {
		// If we don't have a cover picture and clicked the modal to open, this state will initially be true since in the Entry component
		// we return a new component without the isTitleHovered prop but with isModalOpen = true
		const [isModalOpenState, setIsModalOpenState] = useState<boolean>(isModalOpen || false);
		const [isRepositionActive, setIsRepositionActive] = useState<boolean>(false);
		const [initialUnsplashInfoArray, setInitialUnsplashInfoArray] = useState<Array<object>>([]);

		const openModal = () => {
			// set the state for this component (ImagePicker)
			setIsModalOpenState(true);
			void coverPictureState.setIsModalOpen(true);
			// have to check if this function exists since it's an optional prop
			if (setIsModalOpen) {
				// this sets the state in EditorPageCoverPictureEntry
				setIsModalOpen(true);
			}
		};
		const closeModal = () => {
			// set the state for this component (ImagePicker)
			setIsModalOpenState(false);
			void coverPictureState.setIsModalOpen(false);
			// have to check if this function exists since it's an optional prop
			if (setIsModalOpen) {
				// this sets the state in EditorPageCoverPictureEntry
				setIsModalOpen(false);
			}
		};

		if (isUploadingPicture && coverPictureState.state.isUploadingPicture) {
			return (
				<UploadingPictureWrapper
					data-testid="uploading-picture-wrapper"
					hasCoverPicture={hasCoverPicture}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
				>
					<Spinner size="medium" />
				</UploadingPictureWrapper>
			);
		} else if (
			(shouldShowAIUnsplashHeaderToolbar ||
				(coverPictureState.state.shouldShowAIUnsplashHeaderToolbar && !shouldShowReposition)) &&
			hasCoverPicture
		) {
			return (
				<AIUnsplashHeaderToolbar
					onAccept={onAcceptAIUnsplashImage}
					onRegenerate={onRegenerate}
					onRemoveImage={onRemoveImage}
					isRegeneratingImage={isRegeneratingImage}
				/>
			);
		} else if (
			(isRepositionActive ||
				shouldShowReposition ||
				!!coverPictureState.state.shouldShowReposition) &&
			hasCoverPicture
		) {
			// shouldShowReposition = true when we just uploaded a photo
			return (
				<ImageRepositioner
					initialPicturePosition={initialPicturePosition}
					onSaveReposition={onSaveReposition}
					setIsRepositionActive={setIsRepositionActive}
					setShouldShowReposition={setShouldShowReposition}
					coverPictureState={coverPictureState}
					forceUpdate={forceUpdate}
					userOffline={userOffline}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
				/>
			);
		} else if (hasCoverPicture) {
			return (
				<HoverOverlayAndDropdownItems
					userOffline={userOffline}
					closeModal={closeModal}
					openMediaBrowser={openMediaBrowser}
					setIsRepositionActive={setIsRepositionActive}
					onRemoveImage={onRemoveImage}
					handleUnsplashUpload={handleUnsplashUpload}
					setIsUploadingPicture={setIsUploadingPicture}
					initialUnsplashInfoArray={initialUnsplashInfoArray}
					setInitialUnsplashInfoArray={setInitialUnsplashInfoArray}
					coverPictureState={coverPictureState}
					contentId={contentId}
					onImageWidthChange={onImageWidthChange}
					spaceKey={spaceKey}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
					isFixedWidthImageOption={isFixedWidthImageOption}
				/>
			);
		} else {
			// no cover picture
			return (
				<AddHeaderImageButton
					userOffline={userOffline}
					isTitleHovered={isTitleHovered}
					openModal={openModal}
					closeModal={closeModal}
					openMediaBrowser={openMediaBrowser}
					isModalOpenState={isModalOpenState}
					handleUnsplashUpload={handleUnsplashUpload}
					setIsUploadingPicture={setIsUploadingPicture}
					initialUnsplashInfoArray={initialUnsplashInfoArray}
					setInitialUnsplashInfoArray={setInitialUnsplashInfoArray}
					coverPictureState={coverPictureState}
					floatingToolbarButtonStyle={floatingToolbarButtonStyle}
					contentId={contentId}
				/>
			);
		}
	},
);
